import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { getEntitlement, isTwitchSignInRequiredError } from '../api/rttsClient';
import { beginTwitchConnect } from '../auth/beginTwitchConnect';
import { setMockAuthState } from '../api/mockState';
import ReconnectTwitchCard from '../components/ReconnectTwitchCard';
import {
  ENTITLEMENT_POLL_INTERVAL_MS,
  ENTITLEMENT_POLL_MAX_ATTEMPTS,
  STUDIO_COPY,
  STUDIO_DOCUMENT_TITLES,
  isStudioUseMock,
} from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';

type SuccessState =
  | { kind: 'activating'; attempt: number }
  | { kind: 'paid' }
  | { kind: 'pack' }
  | { kind: 'fallback' }
  | { kind: 'reconnect' }
  | { kind: 'error'; message: string };

/**
 * Post-checkout success — never trust the redirect.
 * Re-poll entitlement until paid or capped attempts → calm fallback.
 */
const StudioUpgradeSuccessPage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.upgradeSuccess);
  const [searchParams] = useSearchParams();
  const expectPack = searchParams.get('product') === 'pack5';
  const { status, isLoggedOut, setMockState, refreshMock } = useStudioAuth();
  const [state, setState] = useState<SuccessState>({ kind: 'activating', attempt: 0 });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (): Promise<'paid' | 'pack' | 'free' | 'reconnect' | 'error'> => {
    try {
      const ent = await getEntitlement();
      if (expectPack) {
        if ((ent.reel_credits ?? 0) > 0) return 'pack';
        return 'free';
      }
      if (ent.tier === 'paid') return 'paid';
      return 'free';
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        return 'reconnect';
      }
      return 'error';
    }
  }, [expectPack, refreshMock]);

  const startPolling = useCallback(() => {
    clearTimer();
    cancelledRef.current = false;
    attemptsRef.current = 0;
    setState({ kind: 'activating', attempt: 0 });

    const tick = async () => {
      if (cancelledRef.current) return;
      attemptsRef.current += 1;
      const attempt = attemptsRef.current;
      setState({ kind: 'activating', attempt });

      const result = await pollOnce();
      if (cancelledRef.current) return;

      if (result === 'paid') {
        clearTimer();
        setState({ kind: 'paid' });
        return;
      }
      if (result === 'pack') {
        clearTimer();
        setState({ kind: 'pack' });
        return;
      }
      if (result === 'reconnect') {
        clearTimer();
        setState({ kind: 'reconnect' });
        return;
      }
      if (result === 'error') {
        clearTimer();
        setState({
          kind: 'error',
          message: 'Could not verify your upgrade. Please refresh.',
        });
        return;
      }
      if (attempt >= ENTITLEMENT_POLL_MAX_ATTEMPTS) {
        clearTimer();
        setState({ kind: 'fallback' });
      }
    };

    void tick();
    intervalRef.current = setInterval(() => {
      void tick();
    }, ENTITLEMENT_POLL_INTERVAL_MS);
  }, [clearTimer, pollOnce]);

  useEffect(() => {
    if (isLoggedOut || status === 'loading' || status === 'stale_link') return;
    startPolling();
    return () => {
      cancelledRef.current = true;
      clearTimer();
    };
  }, [isLoggedOut, status, startPolling, clearTimer]);

  if (status === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  if (status === 'stale_link' || state.kind === 'reconnect') {
    return (
      <div>
        <ReconnectTwitchCard
          onReconnect={() => {
            const shouldNavigate = beginTwitchConnect({
              returnTo: '/studio/upgrade/success',
            });
            if (shouldNavigate) {
              setMockState('linked');
              startPolling();
            }
          }}
        />
      </div>
    );
  }

  if (state.kind === 'pack') {
    return (
      <div
        className="studio-panel space-y-6"
        data-testid="studio-upgrade-success-pack"
      >
        <div className="studio-tape-stripe rounded-sm opacity-60" aria-hidden />
        <h1 className="studio-display text-3xl" data-testid="studio-success-pack-headline">
          {STUDIO_COPY.successPackHeadline}
        </h1>
        <p className="studio-page-kicker">{STUDIO_COPY.successPackBody}</p>
        <Link
          to="/studio/vods"
          className="studio-btn-primary no-underline inline-flex"
          data-testid="studio-success-vods-cta"
        >
          {STUDIO_COPY.backToVods}
        </Link>
      </div>
    );
  }

  if (state.kind === 'paid') {
    return (
      <div
        className="studio-panel space-y-6"
        data-testid="studio-upgrade-success"
      >
        <div className="studio-tape-stripe rounded-sm opacity-60" aria-hidden />
        <h1 className="studio-display text-3xl" data-testid="studio-success-pro-headline">
          {STUDIO_COPY.successProHeadline}
        </h1>
        <Link
          to="/studio/vods"
          className="studio-btn-primary no-underline inline-flex"
          data-testid="studio-success-vods-cta"
        >
          {STUDIO_COPY.backToVods}
        </Link>
      </div>
    );
  }

  if (state.kind === 'fallback') {
    return (
      <div
        className="studio-panel space-y-5"
        data-testid="studio-upgrade-success-fallback"
      >
        <h1 className="studio-display text-2xl">{STUDIO_COPY.successAlmostThere}</h1>
        <p className="studio-page-kicker">
          {STUDIO_COPY.successFallback}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="studio-btn-primary"
            onClick={() => startPolling()}
            data-testid="studio-success-refresh"
          >
            {STUDIO_COPY.refreshCta}
          </button>
          <Link to="/studio/vods" className="studio-btn-ghost no-underline inline-flex">
            {STUDIO_COPY.backToVods}
          </Link>
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="studio-panel space-y-4" role="alert">
        <p>{state.message}</p>
        <button type="button" className="studio-btn-ghost" onClick={() => startPolling()}>
          {STUDIO_COPY.refreshCta}
        </button>
      </div>
    );
  }

  return (
    <div
      className="studio-section py-16"
      data-testid="studio-upgrade-activating"
    >
      <div className="studio-spinner" />
      <h1 className="text-2xl font-semibold">{STUDIO_COPY.successActivating}</h1>
      <p className="studio-fine-print">
        {STUDIO_COPY.successRefreshing}
        {state.attempt > 0 ? ` (${state.attempt})` : ''}
      </p>
    </div>
  );
};

export default StudioUpgradeSuccessPage;
